import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, Save, RotateCcw, Tag } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function RoleLabelsAdminPage() {
  const [defaults, setDefaults] = useState({});
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/org/role-labels`, { headers: authHeader() });
      setDefaults(r.data?.defaults || {});
      setOverrides(r.data?.overrides || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load labels');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API_URL}/api/org/role-labels`, { labels: overrides }, { headers: authHeader() });
      toast.success('Role labels saved');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const reset = (k) => {
    const next = { ...overrides };
    delete next[k];
    setOverrides(next);
  };

  if (loading) return <div className="p-12 text-center text-[#64748B]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…</div>;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="role-labels-admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2342] flex items-center gap-2">
            <Tag className="w-5 h-5 text-[#0E7490]" /> Role Labels
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Rename roles to match your organization&apos;s terminology. Defaults are shown in the placeholder.</p>
        </div>
        <Button onClick={save} disabled={busy} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="save-labels-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
        </Button>
      </div>

      <Card className="border-[#E2E8F0]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm text-[#0A2342]">Customize labels</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {Object.entries(defaults).map(([k, def]) => (
            <div key={k} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-center" data-testid={`label-row-${k}`}>
              <div className="text-xs text-[#64748B] font-mono">{k}</div>
              <Input
                placeholder={def}
                value={overrides[k] || ''}
                onChange={(e) => setOverrides({ ...overrides, [k]: e.target.value })}
                data-testid={`label-input-${k}`}
              />
              <button
                type="button"
                onClick={() => reset(k)}
                disabled={!overrides[k]}
                className="text-xs text-[#64748B] hover:text-rose-600 disabled:opacity-40"
                title="Reset to default"
                data-testid={`label-reset-${k}`}
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
